import { useAppDispatch, useAppSelector } from '@app/core/hooks/redux-hooks';
import useSetSelectedTab from '@app/core/hooks/useSetSelectedTab';
import {
  InstrumentDefinitionWithId,
  selectCapitalStructure,
  selectCapitalStructureId,
  selectOrdinaryEquityInstruments,
  selectInstrumentIdsByDividendSet,
  selectNormalisedDividendData,
} from '@app/core/store/capital-structure-slice-selectors';
import {
  setCapitalStructureSelectedTab,
  setCapitalStructureDividendsLastPwermCase,
} from '@app/core/store/ui-values-slice';
import { RouteConstants } from '@app/modules/projects/RouteConstants';
import { formConfigBase } from '@app/shared/constants/form-config-base';
import {
  TABLE_COL_WIDTH_ALTERNATIVE,
  TABLE_LABELS_COL_WIDTH_ALTERNATIVE,
} from '@app/shared/constants/table-config';
import { DividendInstrumentDto, DividendSetDto } from '@app/shared/models/contracts/project-dto';
import { FC, Fragment, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { generatePath, Navigate, useParams } from 'react-router-dom';
import styles from './dividends-table.module.scss';
import CellValue from '@app/shared/components/cell-value/CellValue';
import { shortDateViewRules } from '@app/shared/components/cell-value/CellValueConfigurations';
import classNames from 'classnames';
import useInstrumentSort from '@app/shared/hooks/useInstrumentSort';
import { useLocale } from '@app/core/hooks/useLocale';
import { updateProjectDraft } from '@app/core/store/project-slice';
import { cloneToMutableData, enumKeyByValue } from '@app/shared/helpers';
import useGetHistoricDividendYears from '@app/shared/hooks/useGetHistoricDividendYears';
import { CalcMethod } from '@app/shared/models/contracts/enums/shared-enums';
import InstrumentRow from './InstrumentRow';
import { getInstrumentCreationDates } from '../../utils/CapitalStructureHelper';
import { selectIsCompatibilityMode } from '@app/core/store/project-slice-selectors';

export const DividendsTable: FC = () => {
  const { l } = useLocale();
  useSetSelectedTab(RouteConstants.Dividends, setCapitalStructureSelectedTab);
  const { dividendSetId } = useParams();
  const dispatch = useAppDispatch();
  const historicDividendYears = useGetHistoricDividendYears();
  const projectDraft = useAppSelector((state) => state.project.projectDraft);
  const isOpmOnly = projectDraft.details.calcMethod === CalcMethod.OPM;
  const isPwermAndOpm =
    projectDraft.details.calcMethod === enumKeyByValue(CalcMethod, CalcMethod.PWERM_AND_OPM);
  const capitalStructure = useAppSelector(selectCapitalStructure);
  const capitalStructureId = useAppSelector(selectCapitalStructureId);
  const ordinaryEquityInstruments = useAppSelector(selectOrdinaryEquityInstruments);
  const isCompatibilityMode = useAppSelector(selectIsCompatibilityMode);

  const lastPwermCase = useAppSelector(
    (state) => state.uiValues.userSelections.capitalStructure.dividends.lastPwermCase
  );
  let selectedDividendSetId = dividendSetId;

  useEffect(() => {
    if (isPwermAndOpm && dividendSetId && dividendSetId !== lastPwermCase) {
      const isPwermCase = projectDraft.pwermInput.cases.some(
        (caseItem) => caseItem.dividendSetId === dividendSetId
      );
      if (isPwermCase) {
        dispatch(setCapitalStructureDividendsLastPwermCase(dividendSetId));
      }
    }
  }, [isPwermAndOpm, dividendSetId, lastPwermCase, projectDraft.pwermInput.cases, dispatch]);

  if (isOpmOnly) {
    selectedDividendSetId = projectDraft.opmInput.selectedDividendSetId ?? undefined;
  }

  if (isPwermAndOpm && dividendSetId) {
    const pwermCase = projectDraft.pwermInput.cases.find(
      (caseItem) => caseItem.dividendSetId === dividendSetId
    );
    selectedDividendSetId = pwermCase?.dividendSetId ?? undefined;
  }

  const dividendSetInstrumentIds = useAppSelector(
    selectInstrumentIdsByDividendSet(selectedDividendSetId)
  );

  const caseOrdinaryEquityInstruments = ordinaryEquityInstruments.filter((instrument) =>
    dividendSetInstrumentIds.has(instrument.instrumentId)
  );
  const sortedOrdinaryEquityInstruments = useInstrumentSort(caseOrdinaryEquityInstruments);
  const showHistoricColumns = useAppSelector(
    (state) => state.uiValues.userSelections.capitalStructure.dividends.showHistoricYears
  );
  const dividendEligibleInstruments = sortedOrdinaryEquityInstruments.filter(
    (instrument) => instrument.dividendEligible === true
  );
  const dividendIneligibleInstruments = sortedOrdinaryEquityInstruments.filter(
    (instrument) => instrument.dividendEligible !== true
  );

  const normalisedData = useAppSelector((state) =>
    selectNormalisedDividendData(state, historicDividendYears)
  );
  const historicDividendInstruments = normalisedData.historicDividendInstruments;
  const forecastDividendSets = normalisedData.forecastDividendSets;

  const forecastSet = Object.values(forecastDividendSets).find(
    (set) => set.dividendSetId === selectedDividendSetId
  );

  const formMethods = useForm<{
    historicDividendInstruments: Record<string, DividendInstrumentDto>;
    forecastDividendSets: Record<string, DividendSetDto>;
    instrumentDefinitions: Record<string, Omit<InstrumentDefinitionWithId, 'instrumentId'>>;
  }>({
    ...formConfigBase,
    defaultValues: normalisedData,
  });

  const { trigger, reset, getValues } = formMethods;

  const submitHandler = async () => {
    const data = cloneToMutableData(getValues());
    const updatedProjectDraft = await formatData(data);
    dispatch(updateProjectDraft({ project: updatedProjectDraft }));
  };

  const formatData = async (data: {
    historicDividendInstruments: Record<string, DividendInstrumentDto>;
    forecastDividendSets: Record<string, DividendSetDto>;
    instrumentDefinitions: Record<string, Omit<InstrumentDefinitionWithId, 'instrumentId'>>;
  }) => {
    const { historicDividendInstruments, forecastDividendSets, instrumentDefinitions } = data;

    const originalForecastDividendSets =
      projectDraft.capitalStructures[capitalStructureId].forecastDividendSets;
    const updatedForecastDividendSets = cloneToMutableData(originalForecastDividendSets);

    Object.entries(forecastDividendSets).forEach(([setId, submittedSet]) => {
      if (updatedForecastDividendSets[setId]) {
        updatedForecastDividendSets[setId].instruments = {
          ...updatedForecastDividendSets[setId].instruments,
          ...submittedSet.instruments,
        };
      }
    });

    const originalHistoricDividendInstruments =
      projectDraft.capitalStructures[capitalStructureId].historicDividendInstruments;

    const updatedHistoricDividendInstruments = {
      ...originalHistoricDividendInstruments,
      ...historicDividendInstruments,
    };

    const updatedProjectDraft = {
      ...projectDraft,
      capitalStructures: {
        ...projectDraft.capitalStructures,
        [capitalStructureId]: {
          ...capitalStructure,
          instrumentDefinitions: instrumentDefinitions,
          historicDividendInstruments: updatedHistoricDividendInstruments,
          forecastDividendSets: updatedForecastDividendSets,
        },
      },
    };

    return updatedProjectDraft;
  };

  const handleToggleChange = () => {
    formMethods.handleSubmit(
      () => submitHandler(),
      () => submitHandler()
    )();
  };

  useEffect(() => {
    reset(normalisedData);
    trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dividendSetId]);

  const historicYearsLength = historicDividendYears.length;
  const isOpmDividendSet = projectDraft.opmInput.selectedDividendSetId === selectedDividendSetId;
  const forecastDates = isOpmDividendSet
    ? projectDraft.opmInput.perYearInputs.map((input) => input.forecastDate)
    : projectDraft.pwermInput.cases
        .find((caseItem) => caseItem.dividendSetId === dividendSetId)
        ?.forecasts.map((forecast) => forecast.forecastYear);
  const forecastYearsLength = forecastDates ? forecastDates.length : 0;

  const columnsCount = (showHistoricColumns ? historicYearsLength : 0) + forecastYearsLength;

  const toggleWidth = 40;
  const adjustedNarrativeWidth = TABLE_LABELS_COL_WIDTH_ALTERNATIVE - toggleWidth - 40;

  if (isCompatibilityMode) {
    const newPath = generatePath(`../${RouteConstants.BuildStructure}`);
    return <Navigate to={newPath} />;
  }

  const isDividendSetPwerm = projectDraft.pwermInput.cases.some(
    (caseItem) => caseItem.dividendSetId === dividendSetId
  );
  const isDividendSetOpm = projectDraft.opmInput.selectedDividendSetId === dividendSetId;

  if (dividendSetId && !isDividendSetPwerm && !isDividendSetOpm) {
    const newPath = generatePath(`../../../${RouteConstants.ProjectDetails}`);
    return <Navigate to={newPath} />;
  }

  if (!isOpmOnly && !dividendSetId) {
    return null;
  }

  const instrumentCreationDates = getInstrumentCreationDates(
    dividendSetId,
    sortedOrdinaryEquityInstruments,
    projectDraft,
    capitalStructure
  );

  return (
    <FormProvider {...formMethods}>
      <form
        data-cy="dividends-form"
        onBlur={formMethods.handleSubmit(
          () => submitHandler(),
          () => submitHandler()
        )}>
        <table
          className={classNames(
            'table-primary',
            'table-primary--zebra',
            'table-primary--fixed',
            'table-primary--framed',
            'table-primary--sticky-first-column',
            styles['dividends-table']
          )}
          style={{
            width: columnsCount
              ? columnsCount * TABLE_COL_WIDTH_ALTERNATIVE +
                TABLE_LABELS_COL_WIDTH_ALTERNATIVE +
                'px'
              : undefined,
          }}>
          <colgroup>
            <col style={{ width: `${TABLE_LABELS_COL_WIDTH_ALTERNATIVE}px` }} />
            {showHistoricColumns &&
              [...Array(historicYearsLength)].map((_, index) => (
                <col key={`col-${index}`} style={{ width: `${TABLE_COL_WIDTH_ALTERNATIVE}px` }} />
              ))}
            {showHistoricColumns && <col className="table-primary__col-action--dividend" />}
            {forecastYearsLength > 0 && (
              <>
                {[...Array(forecastYearsLength)].map((_, index) => (
                  <col key={`col-${index}`} style={{ width: `${TABLE_COL_WIDTH_ALTERNATIVE}px` }} />
                ))}
                <col className="table-primary__col-action--dividend" />
              </>
            )}
          </colgroup>
          <thead className="table-primary__sticky-section table-primary__sticky-section--capital-structure-header">
            <tr>
              <th className="table-primary__cell--header-tertiary table-primary__cell--vertical-separator" />
              {showHistoricColumns && (
                <>
                  <th
                    colSpan={historicYearsLength + 1}
                    className="table-primary__cell--header-tertiary table-primary__cell--center table-primary__cell--vertical-separator table-primary__cell--group-separator-vertical">
                    <span className={styles['case-name']}>{l('_Historic')}</span>
                  </th>
                </>
              )}
              <th
                colSpan={forecastYearsLength + 1}
                className="table-primary__cell--header-tertiary table-primary__cell--center table-primary__cell--vertical-separator table-primary__cell--group-separator-vertical">
                <span className={styles['case-name']}>{l('_ForecastTitle')}</span>
              </th>
            </tr>
            <tr>
              <th className="table-primary__cell--header-tertiary table-primary__cell--left table-primary__cell--vertical-separator table-primary__cell--group-separator-vertical table-primary__cell--section-start-separator">
                {l('_Instrument')}
              </th>
              {showHistoricColumns && (
                <>
                  {historicDividendYears.map((date) => (
                    <Fragment key={date + 'historic-year-value'}>
                      <td
                        className={classNames(
                          'table-primary__cell--header-tertiary',
                          'table-primary__cell--center',
                          'table-primary__cell--group-separator-vertical',
                          'table-primary__cell--section-start-separator'
                        )}>
                        <CellValue value={date} {...shortDateViewRules} />
                      </td>
                    </Fragment>
                  ))}
                  <td
                    className={classNames(
                      'table-primary__cell--header-tertiary',
                      'table-primary__cell--center',
                      'table-primary__cell--group-separator-vertical',
                      'table-primary__cell--section-start-separator',
                      'table-primary--framed'
                    )}
                  />
                </>
              )}
              {forecastDates?.map((date) => (
                <Fragment key={date + 'forecast-year-value'}>
                  <td
                    className={classNames(
                      'table-primary__cell--header-tertiary table-primary__cell--center table-primary__cell--group-separator-vertical table-primary__cell--section-start-separator'
                    )}>
                    <CellValue value={date} {...shortDateViewRules} />
                  </td>
                </Fragment>
              ))}
              {forecastYearsLength > 0 && (
                <td
                  className={classNames(
                    'table-primary__cell--header-tertiary table-primary__cell--center table-primary__cell--group-separator-vertical table-primary__cell--section-start-separator'
                  )}
                />
              )}
            </tr>
          </thead>
          <tbody>
            <tr>
              <th className="table-primary__cell--vertical-separator table-primary__cell--strong">
                {l('_DividendEligible')}
              </th>
              {showHistoricColumns && (
                <>
                  {historicDividendYears.map((_, idx) => (
                    <td key={`empty-historic-${idx}`} />
                  ))}
                  <td className="table-primary--framed" />
                </>
              )}
              {forecastDates?.map((_, idx) => (
                <td key={`empty-forecast-${idx}`} />
              ))}
              {forecastYearsLength > 0 && <td />}
            </tr>
            {dividendEligibleInstruments.map((instrument) => (
              <InstrumentRow
                key={instrument.instrumentId}
                instrument={instrument}
                isEligible
                adjustedNarrativeWidth={adjustedNarrativeWidth}
                handleToggleChange={handleToggleChange}
                historicDividendInstruments={historicDividendInstruments}
                forecastSet={forecastSet}
                forecastDates={forecastDates ?? []}
                styles={styles}
                showHistoricColumns={showHistoricColumns}
                instrumentCreationDates={instrumentCreationDates}
              />
            ))}
            <tr>
              <th className="table-primary__cell--vertical-separator table-primary__cell--strong">
                {l('_IneligibleForDividends')}
              </th>
              {showHistoricColumns && (
                <>
                  {historicDividendYears.map((_, idx) => (
                    <td key={`empty-historic-${idx}`} />
                  ))}
                  <td className="table-primary--framed" />
                </>
              )}
              {forecastDates?.map((_, idx) => (
                <td key={`empty-forecast-${idx}`} />
              ))}
              {forecastYearsLength > 0 && <td />}
            </tr>
            {dividendIneligibleInstruments.map((instrument) => (
              <InstrumentRow
                key={instrument.instrumentId}
                instrument={instrument}
                isEligible={false}
                adjustedNarrativeWidth={adjustedNarrativeWidth}
                handleToggleChange={handleToggleChange}
                historicDividendInstruments={historicDividendInstruments}
                forecastSet={forecastSet}
                forecastDates={forecastDates ?? []}
                styles={styles}
                showHistoricColumns={showHistoricColumns}
                instrumentCreationDates={instrumentCreationDates}
              />
            ))}
          </tbody>
        </table>
      </form>
    </FormProvider>
  );
};
